import React, { useEffect, useState } from "react";
import { ArrowRight, Quote, Star, Headset, Mail, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ASSETS, BLOGS, CONTACT, FAQS, TESTIMONIALS } from "@/mock";
import { apiListReviews, apiAddReview } from "@/api";
import { toast } from "sonner";

// ---------------- Blogs ----------------
export const Blogs = () => {
  const [active, setActive] = useState(null);
  return (
  <section className="mx-auto max-w-7xl px-4 pt-14">
    <div className="flex items-end justify-between">
      <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
        Travel <span className="text-[#1B1F5B]">Blogs</span>
      </h2>
    </div>
    <div className="no-scrollbar -mx-4 mt-6 flex gap-5 overflow-x-auto px-4 pb-4 snap-x">
      {BLOGS.map((b, i) => (
        <button
          key={i}
          data-testid={`blog-card-${i}`}
          onClick={() => setActive(b)}
          className="group w-[280px] shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-[0_6px_24px_rgba(27,31,91,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(27,31,91,0.15)]"
        >
          <div className="h-40 overflow-hidden">
            <img
              src={b.img}
              alt={b.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          </div>
          <div className="p-4">
            {b.date && <p className="mb-1 text-[11px] font-semibold text-slate-400">{b.date} · {b.read}</p>}
            <h3 className="line-clamp-2 min-h-[42px] text-sm font-bold leading-snug text-slate-800 transition-colors group-hover:text-[#1B1F5B]">
              {b.title}
            </h3>
            {b.excerpt && <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">{b.excerpt}</p>}
            <span className="mt-3 flex items-center gap-1 text-[13px] font-bold text-[#F5B32B]">
              Read More <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </div>
        </button>
      ))}
    </div>

    <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto p-0" data-testid="blog-modal">
        {active && (
          <>
            <img src={active.img} alt={active.title} className="h-56 w-full object-cover" />
            <div className="p-6">
              <p className="text-[11px] font-semibold text-slate-400">{active.date} · {active.read}</p>
              <DialogHeader>
                <DialogTitle className="mt-1 text-left font-[Poppins] text-xl font-extrabold leading-snug text-slate-800">{active.title}</DialogTitle>
              </DialogHeader>
              <div className="mt-3 space-y-3">
                {(active.body || []).map((p, j) => (
                  <p key={j} className="text-sm leading-relaxed text-slate-600">{p}</p>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  </section>
  );
};

// ---------------- Testimonials ----------------
export const Testimonials = () => {
  const [items, setItems] = useState(TESTIMONIALS);
  const [form, setForm] = useState({ name: "", role: "Customer", text: "" });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiListReviews()
      .then((res) => {
        if (res.reviews && res.reviews.length) {
          setItems(res.reviews.map((r) => ({ img: "", name: r.name, role: r.role, text: r.text })));
        }
      })
      .catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please fill your name and review");
      return;
    }
    apiAddReview({ name: form.name, role: form.role, text: form.text })
      .then(() => {
        setItems([{ img: "", name: form.name, role: form.role, text: form.text }, ...items]);
        setForm({ name: "", role: "Customer", text: "" });
        setOpen(false);
        toast.success("Thank you! Your review has been added.");
      })
      .catch(() => toast.error("Could not submit review. Please try again."));
  };

  return (
    <section className="mt-16 bg-[#1B1F5B]/[0.03] py-14">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex items-end justify-between">
          <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
            What our <span className="text-[#1B1F5B]">clients say</span>
          </h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="rounded-lg bg-[#F5B32B] px-4 py-2 text-sm font-bold text-[#0A0F2E] shadow-sm transition-colors hover:bg-[#E89A10]">
                Add Review
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Share your experience</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]"
                />
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]"
                >
                  <option>Customer</option>
                  <option>Agent</option>
                </select>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="Write your review..."
                  rows={4}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]"
                />
                <button className="w-full rounded-lg bg-[#1B1F5B] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#141844]">
                  Submit Review
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="mt-8">
          <CarouselContent>
            {items.map((t, i) => (
              <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/3">
                <div className="relative h-full rounded-2xl border border-slate-100 bg-white p-6 pt-8 shadow-[0_6px_24px_rgba(27,31,91,0.07)]">
                  <Quote className="absolute right-5 top-5 h-7 w-7 text-[#F5B32B]/40" />
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-[#F5B32B] text-[#F5B32B]" />
                    ))}
                  </div>
                  <p className="mt-3 min-h-[96px] text-[13px] leading-relaxed text-slate-600">
                    {t.text}
                  </p>
                  <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
                    {t.img ? (
                      <img
                        src={t.img}
                        alt={t.name}
                        className="h-11 w-11 rounded-full border-2 border-[#F5B32B]/40 object-cover"
                      />
                    ) : (
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1B1F5B] text-sm font-bold text-white">
                        {t.name.charAt(0)}
                      </span>
                    )}
                    <div>
                      <p className="text-sm font-bold text-slate-800">{t.name}</p>
                      <p className="text-xs text-slate-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-3 border-[#1B1F5B]/20 text-[#1B1F5B]" />
          <CarouselNext className="-right-3 border-[#1B1F5B]/20 text-[#1B1F5B]" />
        </Carousel>
      </div>
    </section>
  );
};

// ---------------- FAQ ----------------
export const FaqSection = () => (
  <section className="mx-auto max-w-7xl px-4 pt-16">
    <h2 className="text-center text-2xl font-extrabold text-slate-800 md:text-[28px]">
      Frequently Asked <span className="text-[#1B1F5B]">Questions ?</span>
    </h2>
    <div className="mt-8 grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
      <img
        src={ASSETS.faqImg}
        alt="FLYTRIP FAQs"
        loading="lazy"
        className="mx-auto w-full max-w-sm rounded-2xl object-contain"
      />
      <Accordion type="single" collapsible className="w-full">
        {FAQS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="mb-2.5 rounded-xl border border-slate-100 bg-white px-4 shadow-sm data-[state=open]:border-[#1B1F5B]/30"
          >
            <AccordionTrigger className="py-3.5 text-left text-sm font-bold text-slate-800 hover:text-[#1B1F5B] hover:no-underline">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="pb-4 text-[13px] leading-relaxed text-slate-500">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ---------------- About + Support strip ----------------
export const AboutStrip = () => (
  <section className="mx-auto max-w-7xl px-4 py-16">
    <div className="grid items-center gap-8 rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_10px_40px_rgba(27,31,91,0.08)] lg:grid-cols-[1.6fr_1fr]">
      <div>
        <h2 className="text-xl font-extrabold text-slate-800 md:text-2xl">
          Search Flights, Hotels, Bus and Holiday <span className="text-[#1B1F5B]">Package</span>
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          {CONTACT.company} was established with a clear vision: to make the
          world accessible to everyone. As a comprehensive online travel agency,
          we proudly offer a variety of travel services and resources designed
          to simplify your entire journey. Our commitment is to leverage
          technology and expertise to deliver value, ensuring the most
          affordable rates for your trips without ever compromising on service
          quality. Choose {CONTACT.company} for simple, smart, and stress-free
          travel planning.
        </p>
      </div>
      <div className="rounded-2xl bg-[#1B1F5B] p-6 text-white">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F5B32B]">
            <Headset className="h-6 w-6 text-[#0A0F2E]" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wider text-white/60">24/7 Customer Support</p>
            <p className="text-lg font-extrabold">We are here to help</p>
          </div>
        </div>
        <div className="mt-4 space-y-2.5 text-sm">
          <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2.5 rounded-lg bg-white/10 px-3.5 py-2.5 transition-colors hover:bg-white/20">
            <Phone className="h-4 w-4 text-[#F5B32B]" /> {CONTACT.phonePretty}
          </a>
          <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 rounded-lg bg-white/10 px-3.5 py-2.5 transition-colors hover:bg-white/20">
            <Mail className="h-4 w-4 text-[#F5B32B]" /> {CONTACT.email}
          </a>
        </div>
      </div>
    </div>
  </section>
);
